'use client';

import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { addDays, differenceInYears, isAfter } from 'date-fns';
import { PrismicRichText } from '@prismicio/react';
import pluralize from 'pluralize';

import {
  Button,
  DatePicker,
  Icon,
  Input,
  Link,
  Modal,
  PrismicImageWithAlt,
  useData,
} from '@/components';
import { useEcomStoreSelector, useForm } from '@/data';
import { AgeGateDocumentDataAgreementsItem } from '@/types';
import { useFixedBodyOnPopup } from '@/hooks';

import styles from './AgeGate.module.scss';

const AgeGate: FC = () => {
  const { terms_accepted, is_subscribed_to_crm, setState } =
    useEcomStoreSelector(['terms_accepted', 'is_subscribed_to_crm']);
  useFixedBodyOnPopup({ isPopupOpen: !terms_accepted });

  const [birthDate, setBirthDate] = useState<Date>();

  const { header, ageGate, crmModal } = useData();

  const [hasIncompleteFields, setHasIncompleteFields] = useState(false);
  const [isYounger, setIsYounger] = useState(false);
  const [agreementsAccepted, setAgreementsAccepted] = useState(true);

  const type = ageGate?.data.type || '';
  const buttonType = ageGate?.data?.button_type;
  const agreements =
    ageGate?.data?.agreements?.filter(({ text }) => !!text) || [];
  const rememberMeDays = ageGate?.data?.remember_me;
  const daysLabel = `${pluralize('day', rememberMeDays || 0)}`;
  const rememberText = `Remember me for ${rememberMeDays} ${daysLabel}.`;
  const allowedAge = ageGate?.data?.allowed_age || 0;
  const delay =
    parseInt(process?.env?.NEXT_PUBLIC_DELAY_CRMMODAL ?? '100') || 100;

  const initialAgreements = {
    ...(rememberMeDays ? { remember_me: true } : {}),
  };

  if (agreements.length) {
    agreements?.forEach((agreement: AgeGateDocumentDataAgreementsItem, id) => {
      initialAgreements[`agreement_${id}`] = agreement.default_state;
    });
  }

  const { inputs, genericChange } = useForm(initialAgreements);

  const errorMessage = useMemo(() => {
    if (hasIncompleteFields) {
      return 'Please enter a valid date of birth.';
    }
    if (isYounger) {
      return `You must be at least ${allowedAge} years old.`;
    }
    if (!agreementsAccepted) {
      return 'You must agree to our terms before continuing.';
    }
    return null;
  }, [hasIncompleteFields, isYounger, agreementsAccepted]);

  useEffect(() => {
    if (rememberMeDays) {
      const expDate = addDays(new Date(), rememberMeDays);
      setState({ terms_accepted_exp_date: expDate });
    }
  }, [rememberMeDays]);

  const handleSubmit = event => {
    event.preventDefault();

    if (!areAgreementsAccepted()) return;

    setState({ terms_accepted: true });

    if (crmModal && is_subscribed_to_crm === null) {
      setTimeout(() => {
        setState({ is_crm_modal_open: true });
      }, delay);
    }
  };

  const handleSubmitDate = event => {
    event.preventDefault();

    if (!birthDate) {
      setHasIncompleteFields(true);
      clearErrorMessage();
      return;
    }

    if (differenceInYears(new Date(), birthDate) < allowedAge) {
      setIsYounger(true);
      clearErrorMessage();
      return;
    }

    handleSubmit(event);
  };

  const areAgreementsAccepted = () => {
    if (!inputs) return true;

    const accepted = Object.values(inputs).every(v => v);
    setAgreementsAccepted(accepted);

    if (!accepted) {
      clearErrorMessage();
      return false;
    }

    return true;
  };

  const handleAgreementChange = event => {
    genericChange(event.target.name, event.target.checked);
  };

  const handleRememberMeCheck = (event: ChangeEvent<HTMLInputElement>) => {
    genericChange(event.target.name, event.target.checked);

    const currentDate = new Date();
    const expDate = addDays(currentDate, rememberMeDays!);

    setState({
      terms_accepted_exp_date: event.target.checked ? expDate : null,
    });
  };

  const clearErrorMessage = () => {
    setTimeout(() => {
      setHasIncompleteFields(false);
      setIsYounger(false);
      setAgreementsAccepted(true);
    }, 4000);
  };

  const logo = ageGate?.data?.logo?.url
    ? ageGate?.data?.logo
    : header?.data?.logo;

  useEffect(() => {
    setState(prevState => {
      if (rememberMeDays === null) {
        return {
          terms_accepted: prevState.terms_accepted,
          terms_accepted_exp_date: null,
        };
      }

      if (rememberMeDays === 0) {
        return { terms_accepted: false, terms_accepted_exp_date: null };
      }

      if (!prevState.terms_accepted_exp_date) {
        return { terms_accepted: false };
      }

      const currentDate = new Date();
      if (isAfter(currentDate, prevState.terms_accepted_exp_date)) {
        return { terms_accepted: false, terms_accepted_exp_date: null };
      }

      return prevState;
    });
  }, [rememberMeDays]);

  return (
    <Modal
      bgImage={ageGate?.data?.background_image}
      className={styles.age}
      data-testid="age-gate-modal"
      isOpen={!terms_accepted}
      overlayBgColor={ageGate?.data?.overlay_background_color as string}
      overlayOpacity={ageGate?.data?.overlay_opacity as number}
      popupBgColor={ageGate?.data?.popup_background_color as string}
      sm
    >
      <figure className={styles.age__image}>
        <PrismicImageWithAlt
          defaultAlt="Brand Logo"
          field={logo}
          height={60}
          style={{ objectFit: 'contain' }}
          width={300}
        />
      </figure>
      <div className={styles.age__message}>
        <PrismicRichText
          components={{
            heading2: ({ children }) => (
              <h2 className={styles.age__heading}>{children}</h2>
            ),
          }}
          field={ageGate?.data.agreement_text}
        />
      </div>
      {type === 'Date of Birth' && (
        <DatePicker onChange={d => setBirthDate(d)} />
      )}
      {!!rememberMeDays && (
        <Input
          checked={inputs.remember_me}
          className={styles.option}
          data-testid="remember-me-checkbox"
          inputLabel={rememberText}
          name="remember_me"
          onChange={handleRememberMeCheck}
          type="checkbox"
        />
      )}
      {agreements && (
        <form>
          {agreements.map(
            (cl, id) =>
              cl?.text && (
                <Input
                  checked={inputs[`agreement_${id}`]}
                  className={styles.option}
                  key={`agreement_${id}`}
                  name={`agreement_${id}`}
                  onChange={handleAgreementChange}
                  type="checkbox"
                >
                  <label>{cl.text}</label>
                </Input>
              ),
          )}
        </form>
      )}
      {!!rememberMeDays && agreements && <hr className={styles.separator} />}
      {errorMessage && (
        <p className={styles.age__error} data-testid="age-gate-error-message">
          <Icon name="info" />
          {errorMessage}
        </p>
      )}
      <div className={styles.age__options}>
        <Button
          color={buttonType ?? 'primary'}
          data-testid="age-gate-submit-button"
          onClick={type === 'Date of Birth' ? handleSubmitDate : handleSubmit}
        >
          {ageGate?.data.button_text}
        </Button>
        <Link
          className="button button_sm button_color-link age__exit"
          href="about:blank"
        >
          Exit the site
        </Link>
      </div>
    </Modal>
  );
};

export default AgeGate;
